import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Coffee, Leaf, Mountain, Wheat, Flame, Droplets, Sparkles, CircleDot } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { CigarAnatomy } from "@/components/Illustrations";
import ChapterRow, { ChapterCta, ManuscriptNote, ChapterBridge } from "./ChapterRow";

/**
 * Chapter IV — MASTER BLENDER
 *
 * The cigar's identity. Three leaves — wrapper, binder, filler — chosen
 * and combined so the smoke reads in one voice. Refactored from the
 * earlier "The Craft" chapter: the chronological process now lives only
 * inside Chapter III (`ChapterJourney`). This chapter is purely about
 * the blender's hand, the anatomy of the cigar, and the signature
 * tasting profile.
 */

const PRIMARY = [
  {
    key: "wrapper",
    origin_en: "Hoja de Capa",
    icons: [Coffee, Sparkles, Leaf],
    icon_labels: ["Cedar", "Cocoa", "Leather"],
    intensity: 4,
  },
  {
    key: "binder",
    origin_en: "Capote",
    icons: [Wheat, Mountain, Droplets],
    icon_labels: ["Earth", "Hay", "Mineral"],
    intensity: 3,
  },
  {
    key: "filler",
    origin_en: "Tripa",
    icons: [Flame, Coffee, Sparkles],
    icon_labels: ["Spice", "Espresso", "Almond"],
    intensity: 5,
  },
];

export default function ChapterCraft() {
  const { t, lang } = useI18n();
  const [active, setActive] = useState("wrapper");
  const activeData = PRIMARY.find((p) => p.key === active) || PRIMARY[0];

  const epigraphEN = (
    <>
      <span className="text-tobacco/90">Why does the blend matter?</span>
      <br />
      Because three leaves must speak with one voice.
    </>
  );
  const epigraphES = (
    <>
      <span className="text-tobacco/90">¿Por qué importa la liga?</span>
      <br />
      Porque tres hojas deben hablar con una sola voz.
    </>
  );

  return (
    <ChapterRow
      id="craft"
      testid="chapter-craft"
      numeral="IV"
      name="MASTER BLENDER"
      chapterLabel={t("master_blender.eyebrow")}
      epigraph={lang === "es" ? epigraphES : epigraphEN}
      headline={t("master_blender.title")}
      body={
        <>
          <p className="text-tobacco/85 text-[15.5px] md:text-[15px] leading-[1.7] font-normal">
            {t("master_blender.intro")}
          </p>
          <p className="text-tobacco/65 italic font-display text-[16px] leading-snug pt-2">
            {t("master_blender.pull")}
          </p>
        </>
      }
      cta={
        <ChapterCta
          testid="craft-cta"
          onClick={() =>
            document.getElementById("collection")?.scrollIntoView({ behavior: "smooth" })
          }
        >
          {lang === "es" ? "Ver el cigarro final" : "See the final cigar"} →
        </ChapterCta>
      }
      illustration={
        <div className="w-full">
          {/* Wrapper / Binder / Filler labels above the cigar */}
          <div className="grid grid-cols-3 mb-2 md:mb-3">
            {PRIMARY.map((p) => {
              const data = t(`science.parts.${p.key}`);
              const isActive = p.key === active;
              return (
                <button
                  key={`top-${p.key}`}
                  type="button"
                  onMouseEnter={() => setActive(p.key)}
                  onFocus={() => setActive(p.key)}
                  data-testid={`science-${p.key}-top`}
                  className="flex flex-col items-center text-center group"
                >
                  <div
                    className={`font-mono text-[10px] tracking-widest2 uppercase transition-colors duration-700 ${
                      isActive ? "text-tobacco" : "text-tobacco/55 group-hover:text-tobacco/85"
                    }`}
                  >
                    {(data.name || "").toUpperCase()}
                  </div>
                  <div
                    className={`mt-0.5 font-display italic text-[12px] md:text-[13px] transition-colors duration-700 ${
                      isActive ? "text-tobacco/85" : "text-tobacco/45"
                    }`}
                  >
                    ({p.origin_en})
                  </div>
                  <span
                    className={`mt-1 w-px h-3 transition-colors duration-700 ${
                      isActive ? "bg-tobacco/70" : "bg-tobacco/20"
                    }`}
                  />
                </button>
              );
            })}
          </div>

          <CigarAnatomy active={active} className="w-full h-auto block" />

          {/* Tasting notes + intensity */}
          <div className="mt-4 grid grid-cols-12 items-center gap-4">
            <div className="col-span-12 md:col-span-3 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55">
              {lang === "es" ? "Notas de cata" : "Tasting Notes"}
            </div>
            <div className="col-span-12 md:col-span-6 flex items-center justify-center gap-5 md:gap-7">
              {activeData.icons.map((Icon, i) => (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <Icon size={18} strokeWidth={1.2} className="text-tobacco/85" />
                  <span className="font-mono text-[8.5px] tracking-widest2 uppercase text-tobacco/65">
                    {activeData.icon_labels[i]}
                  </span>
                </div>
              ))}
            </div>
            <div
              className="col-span-12 md:col-span-3 flex items-center justify-end gap-2"
              data-testid="science-intensity"
            >
              <span className="font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55 mr-1">
                {lang === "es" ? "Intensidad" : "Intensity"}
              </span>
              {Array.from({ length: 5 }).map((_, k) => {
                const filled = k < activeData.intensity;
                return (
                  <CircleDot
                    key={k}
                    size={10}
                    strokeWidth={1.25}
                    className={filled ? "text-tobacco" : "text-tobacco/25"}
                    fill={filled ? "currentColor" : "none"}
                  />
                );
              })}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.p
              key={active}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -2 }}
              transition={{ duration: 0.5 }}
              data-testid={`science-${active}-bottom`}
              className="mt-4 text-center font-display italic text-[15px] md:text-[16px] text-tobacco/75 leading-snug max-w-2xl mx-auto"
            >
              {t(`science.parts.${active}.desc`)}
            </motion.p>
          </AnimatePresence>
        </div>
      }
      meta={
        <ManuscriptNote
          label={lang === "es" ? "Composición" : "Composition"}
          items={[
            { k: lang === "es" ? "Capa" : "Wrapper", v: "Hoja de Capa" },
            { k: lang === "es" ? "Capote" : "Binder", v: "Capote" },
            { k: lang === "es" ? "Tripa" : "Filler", v: "Tripa Larga" },
            { k: lang === "es" ? "Curado" : "Curing", v: "42 d" },
          ]}
        />
      }
      bridge={
        <ChapterBridge
          left={t("master_blender.bridge_left")}
          right={t("master_blender.bridge_right")}
          testid="bridge-iv-to-v"
          onClick={() =>
            document.getElementById("collection")?.scrollIntoView({ behavior: "smooth" })
          }
        />
      }
      bg="bg-pearl"
    />
  );
}
